import React, { useState, useEffect } from 'react';
import { handleNumericInput, handleNumericKeyDown, processNumericInput } from '../utils/inputUtils';
import { Package, Plus, AlertTriangle, Search, Trash2, Edit2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

// Componente de Configuração de Alertas
const ConfiguracaoAlertas = ({ onConfigChange }) => {
  const [diasAlerta, setDiasAlerta] = useState(3);
  const [diasPreAviso, setDiasPreAviso] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/configuracoes/validades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiasAlerta(response.data.dias_alerta_validades || 3);
      setDiasPreAviso(response.data.dias_pre_aviso_validades || 10);
      if (onConfigChange) {
        onConfigChange({
          diasAlerta: response.data.dias_alerta_validades || 3,
          diasPreAviso: response.data.dias_pre_aviso_validades || 10
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (diasPreAviso <= diasAlerta) {
      toast.error('Os dias de pré-aviso devem ser maiores que os dias de alerta urgente');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/configuracoes/validades?dias=${diasAlerta}&dias_pre_aviso=${diasPreAviso}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuração atualizada com sucesso!');
      if (onConfigChange) {
        onConfigChange({ diasAlerta, diasPreAviso });
      }
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm">Carregando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label htmlFor="diasPreAviso" className="text-sm font-medium flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-amber-400"></span>
            Pré-aviso (dias de antecedência)
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Produtos nesta janela aparecem a amarelo como alerta antecipado
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            id="diasPreAviso"
            type="text" inputMode="decimal"
            min="1"
            max="60"
            value={diasPreAviso}
            onChange={(e) => setDiasPreAviso(parseInt(e.target.value.replace(',', '.')) || 0)}
            onKeyDown={handleNumericKeyDown}
            className="w-20"
            data-testid="dias-pre-aviso-input"
          />
          <span className="text-sm text-muted-foreground">dias</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label htmlFor="diasAlerta" className="text-sm font-medium flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
            Alerta urgente (dias de antecedência)
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Produtos nesta janela aparecem a vermelho e disparam alerta ao colaborador no login
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            id="diasAlerta"
            type="text" inputMode="decimal"
            min="0"
            max="30"
            value={diasAlerta}
            onChange={(e) => setDiasAlerta(parseInt(e.target.value.replace(',', '.')) || 0)}
            onKeyDown={handleNumericKeyDown}
            className="w-20"
            data-testid="dias-alerta-input"
          />
          <span className="text-sm text-muted-foreground">dias</span>
        </div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-red-500"></span> Vencido/Urgente</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-amber-400"></span> Pré-aviso</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-green-500"></span> OK</span>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-config-btn">
          {saving ? 'A guardar...' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
};

const ValidadesTab = () => {
  const [produtos, setProdutos] = useState([]);
  const [validades, setValidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingValidade, setEditingValidade] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('alertas'); // alertas, todas
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLoja, setFilterLoja] = useState('');
  const [filterEstado, setFilterEstado] = useState(''); // '', 'ok', 'pre_aviso', 'alerta', 'vencer', 'vencido'
  const [filterDataInicio, setFilterDataInicio] = useState('');
  const [filterDataFim, setFilterDataFim] = useState('');
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  
  // Configuração de alertas (carregada do backend)
  const [configAlerta, setConfigAlerta] = useState({ diasAlerta: 3, diasPreAviso: 10 });
  // Get user from localStorage
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';
  const isAdminOrResponsavel = user?.role === 'admin' || user?.role === 'responsavel';
  
  // Helper para selecionar todo o texto quando foca num input numérico
  const handleFocusSelect = (e) => {
    e.target.select();
    // Se o valor é 0, limpar o campo para facilitar a escrita
    if (e.target.value === '0' || e.target.value === '0.00') {
      setTimeout(() => {
        e.target.value = '';
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  };
  
  const [formData, setFormData] = useState({
    ean: '',
    data_validade: new Date().toISOString().split('T')[0],
    loja: '',
    quantidade: '',
    observacoes: ''
  });
  
  const [produtoEncontrado, setProdutoEncontrado] = useState(null);
  const [artigosSugestao, setArtigosSugestao] = useState([]);
  const [stockXd, setStockXd] = useState(null);
  
  const buscarProdutoPorEAN = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setProdutoEncontrado(null);
      setArtigosSugestao([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar na nova base de ARTIGOS usando o endpoint de search
      const response = await axios.get(
        `${API_URL}/api/artigos/search?q=${encodeURIComponent(searchValue)}&apenas_ativos=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.length > 0) {
        // Mostrar todos os resultados para o utilizador escolher
        const artigos = response.data.map(artigo => ({
          id: artigo.id,
          ean: artigo.ean,
          nome: artigo.descricao,
          isArtigo: true
        }));
        setArtigosSugestao(artigos);
        
        // Se só há um resultado E o EAN corresponde exactamente, seleccionar automaticamente
        if (artigos.length === 1 && artigos[0].ean === searchValue) {
          setProdutoEncontrado(artigos[0]);
          setArtigosSugestao([]);
        }
      } else {
        setProdutoEncontrado(null);
        setArtigosSugestao([]);
      }
    } catch (error) {
      console.error('Erro ao buscar artigo:', error);
      setProdutoEncontrado(null);
      setArtigosSugestao([]);
    }
  };
  
  const selecionarArtigo = async (artigo) => {
    setProdutoEncontrado(artigo);
    setArtigosSugestao([]);
    // Se o artigo tem EAN, mostrar o EAN no input.
    // Caso contrário (produtos sem código de barras como Farinheira/Chouriça),
    // mostrar o nome do artigo para o utilizador ver claramente o que seleccionou
    // e para evitar o bloqueio do `required` HTML5 do input.
    setFormData({...formData, ean: artigo.ean || artigo.nome || ''});
    // Buscar stock XD como referencia (apenas se houver EAN)
    if (!artigo.ean) {
      setStockXd(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/performance-armazem/stock-por-ean/${encodeURIComponent(artigo.ean)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStockXd(res.data);
    } catch {
      setStockXd(null);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [produtosRes, validadesRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/artigos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/validades`, {
          params: { incluir_historico: true },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/configuracoes/validades`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { dias_alerta_validades: 3, dias_pre_aviso_validades: 10 } }))
      ]);
      
      setProdutos(produtosRes.data);
      setValidades(validadesRes.data);
      setConfigAlerta({
        diasAlerta: configRes.data.dias_alerta_validades || 3,
        diasPreAviso: configRes.data.dias_pre_aviso_validades || 10
      });
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };



  const handleReporZeradas = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const { data: sim } = await axios.post(`${API_URL}/api/validades/repor-zeradas-xd`, null, { params: { simular: true }, headers });
      if (!sim.a_repor) {
        toast.info('Não há validades zeradas pela sincronização para repor.');
        return;
      }
      if (!window.confirm(`Repor ${sim.a_repor} validades zeradas pela sincronização XD?\n\n• ${sim.do_registo} com a última quantidade registada pela equipa\n• ${sim.do_stock_xd} sem registo, com o stock atual do XD\n\n• ${sim.sem_stock_xd} sem registo nem stock: voltam a aparecer com 0 para preencher\n\nTodas ficam marcadas "a confirmar".`)) return;
      const { data } = await axios.post(`${API_URL}/api/validades/repor-zeradas-xd`, null, { params: { simular: false }, headers });
      toast.success(`${data.a_repor} validades repostas — confirme as quantidades.`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao repor validades');
    }
  };

  const handleDeleteValidade = async (validadeId, produtoNome) => {
    if (!window.confirm(`Tem certeza que deseja eliminar o registo de validade de "${produtoNome}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/validades/${validadeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Validade eliminada com sucesso');
      loadData(); // Recarregar dados
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao eliminar validade';
      toast.error(errorMsg);
      console.error(error);
    }
  };

  const handleEditValidade = (validade) => {
    setEditingValidade({
      id: validade.id,
      produto_id: validade.produto_id,
      produto_ean: validade.produto?.ean || '',
      produto_nome: validade.produto?.nome || '',
      data_validade: validade.data_validade,
      quantidade: validade.quantidade,
      loja: validade.loja,
      observacoes: validade.observacoes || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingValidade) return;

    try {
      const token = localStorage.getItem('token');
      
      // Atualizar apenas a validade (não o produto)
      // Os campos EAN e nome do produto são apenas para visualização no modal
      await axios.put(
        `${API_URL}/api/validades/${editingValidade.id}`,
        {
          data_validade: editingValidade.data_validade,
          loja: editingValidade.loja,
          observacoes: editingValidade.observacoes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Validade atualizada com sucesso!');
      setShowEditModal(false);
      setEditingValidade(null);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao atualizar validade';
      toast.error(errorMsg);
      console.error(error);
    }
  };


  const handleSubmitValidade = async (e) => {
    e.preventDefault();
    
    if (!produtoEncontrado) {
      toast.error('Por favor, pesquise e selecione um artigo válido');
      return;
    }
    
    if (!formData.loja) {
      toast.error('Por favor, selecione a loja');
      return;
    }

    // Quantidade SEMPRE obrigatória — representa só o lote daquela validade
    // (não o stock total do produto). O auto-fill do XD foi removido por
    // distorcer relatórios.
    const qtyParsed = formData.quantidade !== '' && formData.quantidade !== null
      ? parseInt(formData.quantidade, 10)
      : null;
    if (qtyParsed === null || isNaN(qtyParsed) || qtyParsed <= 0) {
      toast.error('Indique a quantidade do lote (apenas as unidades com esta validade, NÃO o stock total).');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const payload = {
        data_validade: formData.data_validade,
        loja: formData.loja,
        observacoes: formData.observacoes || null,
        quantidade: qtyParsed,
      };
      
      // Se veio da base de artigos, usar artigo_id; senão usar produto_id
      if (produtoEncontrado.isArtigo) {
        payload.artigo_id = produtoEncontrado.id;
      } else {
        payload.produto_id = produtoEncontrado.id;
      }
      
      await axios.post(`${API_URL}/api/validades`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Validade registada com sucesso!');
      setShowModal(false);
      setFormData({ ean: '', data_validade: new Date().toISOString().split('T')[0], loja: '', quantidade: '', observacoes: '' });
      setProdutoEncontrado(null);
      setArtigosSugestao([]);
      setStockXd(null);
      loadData();
    } catch (error) {
      console.error('Erro ao registar validade:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registar validade');
    }
  };

  const calcularDiasRestantes = (dataValidade) => {
    // Criar datas sem hora (apenas dia/mês/ano) para comparação correta
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas a data
    
    const dataVal = new Date(dataValidade);
    dataVal.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas a data
    
    const dias = Math.floor((dataVal - hoje) / (1000 * 60 * 60 * 24));
    return dias;
  };

  const getEstadoColor = (dias) => {
    if (dias < 0) return 'text-red-700 bg-red-100';
    if (dias === 0) return 'text-red-600 bg-red-50';
    if (dias <= configAlerta.diasAlerta) return 'text-orange-600 bg-orange-50';
    if (dias <= configAlerta.diasPreAviso) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  const getEstadoTexto = (dias) => {
    if (dias < 0) return 'Vencido';
    if (dias === 0) return 'Expira hoje';
    if (dias <= configAlerta.diasAlerta) return `Urgente (${dias}d)`;
    if (dias <= configAlerta.diasPreAviso) return `Pré-aviso (${dias}d)`;
    return 'OK';
  };

  // Filter validities based on search and filters
  const filteredValidades = validades.filter(v => {
    // Por defeito, esconder historico (quantidade = 0)
    if (!mostrarHistorico && v.quantidade <= 0 && !v.a_confirmar) return false;
    
    // Search by EAN or product name
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesEAN = v.produto?.ean?.toLowerCase().includes(searchLower);
      const matchesName = v.produto?.nome?.toLowerCase().includes(searchLower);
      if (!matchesEAN && !matchesName) return false;
    }
    
    // Filter by store - a loja está na validade, não no produto
    if (filterLoja && v.loja !== filterLoja) return false;
    
    // Filter by date range
    if (filterDataInicio) {
      if (new Date(v.data_validade) < new Date(filterDataInicio)) return false;
    }
    if (filterDataFim) {
      if (new Date(v.data_validade) > new Date(filterDataFim)) return false;
    }
    
    // Filter by status
    if (filterEstado) {
      const dias = calcularDiasRestantes(v.data_validade);
      if (filterEstado === 'vencido' && dias >= 0) return false;
      if (filterEstado === 'vencer' && (dias < 0 || dias > configAlerta.diasAlerta)) return false;
      if (filterEstado === 'pre_aviso' && (dias <= configAlerta.diasAlerta || dias > configAlerta.diasPreAviso)) return false;
      if (filterEstado === 'ok' && dias <= configAlerta.diasPreAviso) return false;
    }
    
    return true;
  });

  // Group validities by store for alerts view
  const alertasHoje = validades.filter(v => {
    if (v.quantidade <= 0 && !v.a_confirmar) return false;
    const dias = calcularDiasRestantes(v.data_validade);
    return dias <= configAlerta.diasPreAviso; // Show products within pre-warning window
  });

  const alertasPorLoja = alertasHoje.reduce((acc, v) => {
    const loja = v.loja || 'Sem loja';
    if (!acc[loja]) acc[loja] = [];
    acc[loja].push(v);
    return acc;
  }, {});


  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Validades</h2>
          <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Operacional
          </Badge>
        </div>
        <Button onClick={() => setShowModal(true)} size="sm" data-testid="registar-validade-btn">
          <Plus className="h-4 w-4 mr-2" />
          Registar Validade
        </Button>
      </div>

      {/* Tabs internas */}
      <div className="flex gap-1 border-b border-border pb-0" data-testid="validades-tabs">
        <button
          onClick={() => setActiveSubTab('alertas')}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeSubTab === 'alertas'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
          data-testid="validades-tab-alertas"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Alertas ({alertasHoje.length})
        </button>
        <button
          onClick={() => setActiveSubTab('todas')}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeSubTab === 'todas'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
          data-testid="validades-tab-todas"
        >
          <Package className="h-3.5 w-3.5" />
          Activas ({validades.filter(v => v.quantidade > 0 || v.a_confirmar).length})
        </button>
      </div>

      {/* Alertas de Hoje View */}
      {activeSubTab === 'alertas' && (
        <div className="space-y-4">
          {/* Configuração de Alertas (apenas admin) */}
          {isAdmin && (
            <Card className="bg-blue-50/50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-blue-600" />
                  Configuração de Alertas de Validades
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ConfiguracaoAlertas onConfigChange={setConfigAlerta} />
              </CardContent>
            </Card>
          )}
          
          {Object.keys(alertasPorLoja).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-lg font-semibold">Nenhum alerta para hoje! 🎉</p>
                <p className="text-sm text-muted-foreground">Todos os produtos estão dentro do prazo de validade</p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(alertasPorLoja).map(([loja, items]) => (
              <Card key={loja}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {loja} - {items.length} produto(s)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {items.map(v => {
                      const dias = calcularDiasRestantes(v.data_validade);
                      const expiraHoje = dias === 0;
                      return (
                        <div key={v.id} className="flex items-center justify-between p-3 bg-accent/5 rounded-lg">
                          <div className="flex-1">
                            <p className="font-semibold">{v.produto?.nome}</p>
                            <p className="text-sm text-muted-foreground">EAN: {v.produto?.ean}</p>
                            <p className="text-sm">Validade: {new Date(v.data_validade).toLocaleDateString('pt-PT')}</p>
                            {expiraHoje && (
                              <p className="text-xs text-orange-600 font-semibold mt-1 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Expira hoje!
                              </p>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <div>
                              <p className="text-sm font-medium">Quantidade: {v.quantidade}{v.a_confirmar && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">a confirmar</span>}</p>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getEstadoColor(dias)}`}>
                                {getEstadoTexto(dias)}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditValidade(v)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                title="Editar"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteValidade(v.id, v.produto?.nome)}
                                className="border-red-500 text-red-600 hover:bg-red-50"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Todas as Validades View */}
      {activeSubTab === 'todas' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm">Pesquisar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="EAN ou nome do produto..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Loja</Label>
                  <select
                    value={filterLoja}
                    onChange={(e) => setFilterLoja(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Todas as lojas</option>
                    <option value="Café">Café</option>
                    <option value="Loja do Avelal">Loja do Avelal</option>
                    <option value="Loja do Sátão">Loja do Sátão</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Estado</Label>
                  <select
                    value={filterEstado}
                    onChange={(e) => setFilterEstado(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Todos os estados</option>
                    <option value="vencido">Vencido</option>
                    <option value="vencer">Urgente (≤{configAlerta.diasAlerta} dias)</option>
                    <option value="pre_aviso">Pré-aviso ({configAlerta.diasAlerta+1}-{configAlerta.diasPreAviso} dias)</option>
                    <option value="ok">OK (&gt;{configAlerta.diasPreAviso} dias)</option>
                  </select>
                </div>
              </div>
              
              {/* Filtros de Data */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label className="text-sm">Data Validade - De</Label>
                  <Input
                    type="date"
                    value={filterDataInicio}
                    onChange={(e) => setFilterDataInicio(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label className="text-sm">Data Validade - Até</Label>
                  <Input
                    type="date"
                    value={filterDataFim}
                    onChange={(e) => setFilterDataFim(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilterDataInicio('');
                      setFilterDataFim('');
                      setSearchTerm('');
                      setFilterLoja('');
                      setFilterEstado('');
                    }}
                    className="w-full"
                  >
                    Limpar Filtros
                  </Button>
                  <Button
                    variant={mostrarHistorico ? 'default' : 'outline'}
                    onClick={() => setMostrarHistorico(!mostrarHistorico)}
                    className="w-full text-xs whitespace-nowrap"
                    data-testid="toggle-historico-validades"
                  >
                    {mostrarHistorico ? 'Esconder Historico' : 'Mostrar Historico (qty=0)'}
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={handleReporZeradas}
                      className="w-full text-xs whitespace-nowrap border-amber-400 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                      data-testid="repor-validades-zeradas-btn"
                    >
                      Repor zeradas pelo XD
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Validades */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Validades ({filteredValidades.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">EAN</th>
                      <th className="text-left p-3">Produto</th>
                      <th className="text-left p-3">Loja</th>
                      <th className="text-left p-3">Data Validade</th>
                      <th className="text-right p-3">Quantidade</th>
                      <th className="text-center p-3">Estado</th>
                      <th className="text-left p-3">Registado por</th>
                      <th className="text-center p-3">Accoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredValidades.map(v => {
                      const dias = calcularDiasRestantes(v.data_validade);
                      
                      return (
                        <tr key={v.id} className="border-b hover:bg-accent/5">
                          <td className="p-3 font-mono text-sm">{v.produto?.ean}</td>
                          <td className="p-3">{v.produto?.nome}</td>
                          <td className="p-3">{v.loja}</td>
                          <td className="p-3">{new Date(v.data_validade).toLocaleDateString('pt-PT')}</td>
                          <td className="p-3 text-right">
                            {v.quantidade}
                            {v.a_confirmar && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid={`validade-a-confirmar-${v.id}`}>
                                a confirmar
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getEstadoColor(dias)}`}>
                              {getEstadoTexto(dias)}
                            </span>
                          </td>
                          <td className="p-3">
                            {v.criado_por_nome ? (
                              <div className="text-xs">
                                <div className="font-medium text-gray-700">{v.criado_por_nome}</div>
                                {v.created_at && (
                                  <div className="text-gray-500">
                                    {new Date(v.created_at).toLocaleDateString('pt-PT')}, {new Date(v.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditValidade(v)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                title="Editar validade"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteValidade(v.id, v.produto?.nome)}
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                  title="Eliminar validade"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredValidades.length === 0 && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-muted-foreground">
                          Nenhuma validade encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}



      {/* Modal Registar Validade */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Registar Nova Validade</h2>
            </div>
            <form onSubmit={handleSubmitValidade} className="p-6 space-y-4">
              <div>
                <Label>Código EAN ou Nome do Produto *</Label>
                <Input
                  value={formData.ean}
                  onChange={(e) => {
                    setFormData({...formData, ean: e.target.value});
                    setProdutoEncontrado(null); // Limpar selecção anterior
                    buscarProdutoPorEAN(e.target.value);
                  }}
                  placeholder="Digite o EAN ou pesquise pelo nome..."
                  required
                  autoFocus
                />
                
                {/* Lista de sugestões clicáveis */}
                {artigosSugestao.length > 0 && !produtoEncontrado && (
                  <div className="mt-2 border border-blue-200 rounded-lg overflow-hidden bg-white shadow-lg max-h-48 overflow-y-auto">
                    <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                      <span className="text-xs text-blue-700 font-medium">
                        📋 {artigosSugestao.length} artigo(s) encontrado(s) - clique para selecionar:
                      </span>
                    </div>
                    {artigosSugestao.map((artigo, idx) => (
                      <div
                        key={artigo.id || idx}
                        onClick={() => selecionarArtigo(artigo)}
                        className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-sm">{artigo.nome}</div>
                        <div className="text-xs text-gray-500">EAN: {artigo.ean}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Artigo seleccionado */}
                {produtoEncontrado && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                        ✓
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-green-800">{produtoEncontrado.nome}</div>
                        <div className="text-xs text-green-600">EAN: {produtoEncontrado.ean}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setProdutoEncontrado(null);
                          setStockXd(null);
                          setFormData({...formData, ean: ''});
                        }}
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        ✕ Alterar
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Artigo não encontrado */}
                {formData.ean.length >= 2 && !produtoEncontrado && artigosSugestao.length === 0 && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <span>⚠️</span>
                      <span className="font-semibold">Artigo não encontrado na base de dados</span>
                    </div>
                    <div className="text-xs text-yellow-700 mt-2 flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      <span>Por favor, crie o artigo primeiro no menu <strong>&quot;Artigos&quot;</strong> antes de registar a validade.</span>
                    </div>
                  </div>
                )}
                
                {/* Stock XD como referencia */}
                {produtoEncontrado && stockXd && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs" data-testid="stock-xd-referencia">
                    <div className="font-medium text-blue-700 mb-1">Stock actual no XD (referencia):</div>
                    <div className="flex flex-wrap gap-2">
                      {stockXd.por_armazem && stockXd.por_armazem.length > 0 ? (
                        stockXd.por_armazem.map((arm, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 rounded text-blue-800">
                            {arm.armazem_nome || arm.loja}: <strong>{Math.round(arm.stock_atual)}</strong>
                          </span>
                        ))
                      ) : (
                        <span className="text-blue-600">Sem dados de stock no cache XD</span>
                      )}
                      <span className="px-2 py-0.5 bg-blue-200 rounded text-blue-900 font-semibold">
                        Total: {Math.round(stockXd.total_stock)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Loja *</Label>
                <select
                  value={formData.loja}
                  onChange={(e) => {
                    setFormData({...formData, loja: e.target.value});
                  }}
                  className={`w-full px-3 py-2 border rounded-md ${!formData.loja ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  required
                  data-testid="loja-select"
                >
                  <option value="">Selecione a loja</option>
                  <option value="Café">Café</option>
                  <option value="Loja do Avelal">Loja do Avelal</option>
                  <option value="Loja do Sátão">Loja do Sátão</option>
                </select>
                {!formData.loja && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ A loja é obrigatória
                  </p>
                )}
              </div>
              <div>
                <Label>Data de Validade</Label>
                <Input
                  type="date"
                  value={formData.data_validade}
                  onChange={(e) => setFormData({...formData, data_validade: e.target.value})}
                  required
                />
              </div>
              {/* Quantidade — sempre obrigatória, indica APENAS o lote desta validade */}
              {(() => {
                let qtyXd = null;
                if (formData.loja && stockXd && stockXd.por_armazem) {
                  const LOJA_TO_XD = {
                    'Café': 'Cafe',
                    'Loja do Avelal': 'Minimercado',
                    'Loja do Sátão': 'Satao',
                  };
                  const xdName = LOJA_TO_XD[formData.loja] || formData.loja;
                  const match = stockXd.por_armazem.find(
                    arm => (arm.armazem_nome || '').toLowerCase() === xdName.toLowerCase()
                  );
                  if (match) qtyXd = Math.round(match.stock_atual);
                }
                return (
                  <div data-testid="quantidade-field-wrapper">
                    <Label>Quantidade do lote *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Ex: 20"
                      value={formData.quantidade}
                      onChange={(e) => setFormData({...formData, quantidade: e.target.value})}
                      required
                      data-testid="quantidade-input"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                      ⚠️ Indique <strong>apenas as unidades com esta validade específica</strong>, NÃO o stock total do produto.
                      {qtyXd !== null && <> Stock total no XD: <strong>{qtyXd}</strong> un. (referência apenas)</>}
                    </p>
                  </div>
                );
              })()}
              <div>
                <Label>Observações (opcional)</Label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  rows="2"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Registar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal de Editar Validade */}
      {showEditModal && editingValidade && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Editar Validade</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Código EAN</Label>
                <Input
                  value={editingValidade.produto_ean}
                  onChange={(e) => setEditingValidade({...editingValidade, produto_ean: e.target.value})}
                  placeholder="Código EAN do produto"
                />
              </div>
              <div>
                <Label>Nome do Produto</Label>
                <Input
                  value={editingValidade.produto_nome}
                  onChange={(e) => setEditingValidade({...editingValidade, produto_nome: e.target.value})}
                  placeholder="Nome do produto"
                  required
                />
              </div>
              <div>
                <Label>Loja</Label>
                <select
                  value={editingValidade.loja}
                  onChange={(e) => setEditingValidade({...editingValidade, loja: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="">Selecione a loja</option>
                  <option value="Café">Café</option>
                  <option value="Loja do Avelal">Loja do Avelal</option>
                  <option value="Loja do Sátão">Loja do Sátão</option>
                </select>
              </div>
              <div>
                <Label>Data de Validade</Label>
                <Input
                  type="date"
                  value={editingValidade.data_validade}
                  onChange={(e) => setEditingValidade({...editingValidade, data_validade: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Quantidade (stock XD)</Label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800 font-medium">
                    {editingValidade.quantidade} un. <span className="text-xs text-blue-600">(atualiza automaticamente do XD)</span>
                  </p>
                </div>
              </div>
              <div>
                <Label>Observações</Label>
                <textarea
                  value={editingValidade.observacoes}
                  onChange={(e) => setEditingValidade({...editingValidade, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  rows="3"
                  placeholder="Observações sobre a validade..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingValidade(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleSaveEdit}>
                  Guardar Alterações
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidadesTab;
